/* Rail card — the base every card that sits above the bottom rail is built on (Sep 2026).
   Cards: <offer-card> (assets/offer-card.js, the first-order offer) and <discount-card> (assets/discount-card.js,
   the Build-Your-Bundle ladder). Markup, styling and copy for both: sections/bottom-rail.liquid (.rail-card__*).

   What the base owns, so the cards cannot drift from each other:
   - Placement. A card sits ABOVE its capsule, never over it (Anthony, Sep 22): the capsule's left and width, its
     bottom edge one rail inset (12px) above the capsule's top, so the two shapes sit with the rail's own gap
     between them and the capsule stays live underneath. Desktop (≥1000px) that is the ticker capsule, which
     carries the triggers; below 1000px the ticker is not rendered, so it is the Ami capsule.
   - Open and close. No veil, no scroll lock. Escape and a click outside close it; so does another card taking
     the screen (Ami, a header card, a Focal drawer, the explainer). One rail card at a time: opening one closes
     the other. Rendered hidden and unhidden only once placed, the handshake <ami-chat> uses.
   - Triggers. Every [data-action] whose value is one of the card's triggerActions() opens it (or closes it when
     it is open), bound by delegation so a rail re-rendered by the theme editor keeps working; aria-expanded and
     aria-controls are written by the base.
   - Analytics. "<name> Shown" / "<name> Closed" to PostHog, with the card's own props.

   A card overrides the hooks (triggerActions, eventName, openProps, onWire, onOpen, onClose) and nothing else.
   Loaded before the cards (layout/theme.liquid); each card bails if window.RailCard is missing. */

(() => {
  'use strict';

  const COMPACT = window.matchMedia('(max-width: 999px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const RAIL_GAP_PX = 12; // the rail's --rail-inset: the gap between the card and its capsule
  const CLOSE_FADE_MS = 200; // the .rail-card transition, plus a frame

  const openCards = new Set();

  function track(event, props) {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(event, props);
      }
    } catch {
      /* analytics never break a card */
    }
  }

  /* The theme's own notion of "something is open": Focal's drawers and the header cards lock the page with
     lock-all, Ami marks the root with s2-ami-open, the (retired) discount modal marked the body. Rail cards are
     deliberately not counted: they are the thing asking. */
  function somethingElseIsOpen() {
    const root = document.documentElement.classList;
    if (root.contains('lock-all') || root.contains('s2-ami-open')) return true;
    if (document.body.classList.contains('discount-modal-open')) return true;
    if (document.querySelector('.dsc-panel[open]')) return true;
    return false;
  }

  class RailCard extends HTMLElement {
    /* ---------- hooks ---------- */

    /** data-action values that open this card */
    triggerActions() {
      return [];
    }
    /** "Offer Card" → "Offer Card Shown" / "Offer Card Closed" */
    eventName() {
      return 'Rail Card';
    }
    /** Extra props on the Shown event */
    openProps() {
      return {};
    }
    onWire() {}
    onOpen() {}
    onClose() {}

    /* ---------- lifecycle ---------- */

    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      /* Never name an element reference `title`, `hidden`, `id` or `dir`: those shadow HTMLElement properties
         (the <discover-hero> trap, assets/discover.js). */
      this.closeEl = this.querySelector('[data-card-close]');
      this.designMode = this.hasAttribute('data-card-design-mode');
      this.isOpen = false;
      this.openedBy = null;

      if (this.closeEl) this.closeEl.addEventListener('click', () => this.close('close'));

      this._onDocClick = (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const trigger = target && target.closest('[data-action]');
        if (!trigger || !this.isTrigger(trigger)) return;
        event.preventDefault();
        if (this.isOpen) {
          this.close('trigger');
        } else {
          this.open('rail');
        }
      };
      document.addEventListener('click', this._onDocClick);

      this._onDocKeydown = (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close('escape');
      };
      document.addEventListener('keydown', this._onDocKeydown);

      /* A click anywhere outside the card and its triggers closes it. pointerdown, so a click that opens another
         card closes this one before that card measures anything. */
      this._onDocPointerDown = (event) => {
        if (!this.isOpen) return;
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        if (this.contains(target)) return;
        const trigger = target.closest('[data-action]');
        if (trigger && this.isTrigger(trigger)) return;
        this.close('outside');
      };
      document.addEventListener('pointerdown', this._onDocPointerDown);

      /* Another card opening (Ami, a header card, a Focal drawer) takes the screen: this one gets out of the way.
         Header cards and drawers announce themselves with openable-element:open — but so does every Focal
         <collapsible-content> (the collection description, the facet groups) as it expands, including on page
         load, so the event alone is not evidence (gotchas). Look a frame later at what is actually open. */
      this._onOtherOpen = (event) => {
        if (!this.isOpen || event.target === this) return;
        window.requestAnimationFrame(() => {
          if (this.isOpen && somethingElseIsOpen()) this.close('other-card');
        });
      };
      document.addEventListener('openable-element:open', this._onOtherOpen);
      this._rootObserver = new MutationObserver(() => {
        if (this.isOpen && document.documentElement.classList.contains('s2-ami-open')) this.close('ami');
      });
      this._rootObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

      this._onResize = () => {
        if (this.isOpen) this.schedulePosition();
      };
      window.addEventListener('resize', this._onResize, { passive: true });
      if (typeof COMPACT.addEventListener === 'function') COMPACT.addEventListener('change', this._onResize);

      this.setTriggerState(false);
      this.onWire();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onDocKeydown);
      document.removeEventListener('pointerdown', this._onDocPointerDown);
      document.removeEventListener('openable-element:open', this._onOtherOpen);
      window.removeEventListener('resize', this._onResize);
      if (typeof COMPACT.removeEventListener === 'function') COMPACT.removeEventListener('change', this._onResize);
      if (this._rootObserver) this._rootObserver.disconnect();
      openCards.delete(this);
      this.setTriggerState(false);
    }

    isTrigger(el) {
      return this.triggerActions().includes(el.getAttribute('data-action'));
    }

    triggerEls() {
      const actions = this.triggerActions();
      return Array.from(document.querySelectorAll('[data-action]')).filter((el) =>
        actions.includes(el.getAttribute('data-action'))
      );
    }

    setTriggerState(isOpen) {
      this.triggerEls().forEach((el) => {
        el.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (this.id) el.setAttribute('aria-controls', this.id);
      });
    }

    /* ---------- placement ---------- */

    /* The capsule the card sits above: the ticker where it is rendered (desktop), otherwise Ami. */
    anchor() {
      const rail = document.querySelector('bottom-rail');
      if (!rail) return null;
      if (!COMPACT.matches) {
        const ticker = rail.querySelector('.bottom-rail__capsule--ticker');
        if (ticker && ticker.getBoundingClientRect().width > 0) return ticker;
      }
      const ami = rail.querySelector('.bottom-rail__capsule--ami');
      if (ami && ami.getBoundingClientRect().width > 0) return ami;
      return null;
    }

    schedulePosition() {
      if (this.rafPosition) return;
      this.rafPosition = window.requestAnimationFrame(() => {
        this.rafPosition = 0;
        this.position();
      });
    }

    /* The capsule's left and width; the card's bottom edge one rail inset above the capsule's top. The card
       never grows taller than the room above it minus another inset. Without a rail (section removed in the
       editor) the card falls back to the stylesheet's resting position at the foot of the window. */
    position() {
      const anchor = this.anchor();
      if (!anchor) {
        this.style.cssText = '';
        return;
      }
      const rect = anchor.getBoundingClientRect();
      this.style.left = `${Math.round(rect.left)}px`;
      this.style.width = `${Math.round(rect.width)}px`;
      this.style.bottom = `${Math.round(window.innerHeight - rect.top + RAIL_GAP_PX)}px`;
      this.style.top = 'auto';
      this.style.right = 'auto';
      this.style.maxHeight = `${Math.max(200, Math.round(rect.top - RAIL_GAP_PX * 2))}px`;
    }

    /* ---------- open / close ---------- */

    open(by) {
      if (this.isOpen) return;
      openCards.forEach((card) => {
        if (card !== this) card.close('other-card');
      });
      this.isOpen = true;
      this.openedBy = by || 'rail';
      openCards.add(this);
      this.position();
      this.hidden = false; // rendered hidden so the UA sheet keeps it off screen until it is placed
      this.clientWidth; // flush layout so the fade runs
      this.setAttribute('open', '');
      this.setTriggerState(true);
      this.dispatchEvent(new CustomEvent('openable-element:open', { bubbles: true }));
      track(`${this.eventName()} Shown`, { trigger: this.openedBy, ...this.openProps() });
      this.onOpen(this.openedBy);
    }

    close(by) {
      if (!this.isOpen) return;
      this.isOpen = false;
      openCards.delete(this);
      this.removeAttribute('open');
      this.setTriggerState(false);
      const hide = () => {
        if (!this.isOpen) this.hidden = true;
      };
      if (REDUCED_MOTION.matches) hide();
      else window.setTimeout(hide, CLOSE_FADE_MS);
      this.dispatchEvent(new CustomEvent('openable-element:close', { bubbles: true }));
      track(`${this.eventName()} Closed`, { by: by || 'unknown', opened_by: this.openedBy });
      this.onClose(by || 'unknown');
    }
  }

  window.RailCard = { RailCard, COMPACT, REDUCED_MOTION, RAIL_GAP_PX, track, somethingElseIsOpen };
})();
